import { KeyboardEvent, useEffect, useMemo, useState } from "react";
import { CheckCircle2, GitMerge, Loader2, Trash2, Check, Plus } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Input } from "@/components/ui/input";
import {
  libraryCategoryPalette,
  type LibraryCategory,
  type LibraryCategoryColorKey,
} from "@/lib/libraryCategories";
import { cn } from "@/lib/utils";
import { LibraryCategoryBadge } from "./LibraryCategoryBadge";

type LibraryCategoryManagerProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  categories: LibraryCategory[];
  defaultCategoryId: string | null;
  usageCounts?: Record<string, number>;
  onUpdate: (categoryId: string, values: { name: string; color: LibraryCategoryColorKey }) => Promise<void>;
  onDelete: (categoryId: string) => Promise<void>;
  onMerge?: (sourceCategoryId: string, targetCategoryId: string) => Promise<void>;
  onSetDefault: (categoryId: string) => Promise<void>;
  onCreate?: (values: { name: string; color: LibraryCategoryColorKey }) => Promise<unknown>;
};

export const LibraryCategoryManager = ({
  open,
  onOpenChange,
  categories,
  defaultCategoryId,
  usageCounts,
  onUpdate,
  onDelete,
  onMerge,
  onSetDefault,
  onCreate,
}: LibraryCategoryManagerProps) => {
  const initialDrafts = useMemo(
    () => Object.fromEntries(categories.map((category) => [category.id, { name: category.name, color: category.color as LibraryCategoryColorKey }])),
    [categories],
  );
  const [drafts, setDrafts] = useState<Record<string, { name: string; color: LibraryCategoryColorKey }>>(initialDrafts);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [defaultingId, setDefaultingId] = useState<string | null>(null);
  const [pendingMerge, setPendingMerge] = useState<LibraryCategory | null>(null);
  const [mergeTargetId, setMergeTargetId] = useState<string | null>(null);
  const [merging, setMerging] = useState(false);
  const [newName, setNewName] = useState("");
  const [newColor, setNewColor] = useState<LibraryCategoryColorKey>(libraryCategoryPalette[0].value);
  const [creating, setCreating] = useState(false);

  const handleCreate = async () => {
    const name = newName.trim();
    if (!name || !onCreate) return;
    if (categories.some((c) => c.name.trim().toLowerCase() === name.toLowerCase())) {
      toast.error(`"${name}" already exists`);
      return;
    }
    setCreating(true);
    try {
      await onCreate({ name, color: newColor });
      setNewName("");
      toast.success("Category created");
    } catch (error) {
      toast.error((error as Error).message);
    } finally {
      setCreating(false);
    }
  };

  useEffect(() => {
    setDrafts(initialDrafts);
  }, [initialDrafts, open]);

  const updateDraft = (categoryId: string, values: Partial<{ name: string; color: LibraryCategoryColorKey }>) => {
    setDrafts((current) => ({
      ...current,
      [categoryId]: {
        ...current[categoryId],
        ...values,
      },
    }));
  };

  const commitUpdate = async (category: LibraryCategory, nextValues: { name: string; color: LibraryCategoryColorKey }) => {
    const normalizedName = nextValues.name.trim();
    if (!normalizedName) {
      setDrafts((current) => ({
        ...current,
        [category.id]: { name: category.name, color: category.color as LibraryCategoryColorKey },
      }));
      toast.error("Category name is required");
      return;
    }

    if (normalizedName === category.name && nextValues.color === category.color) return;

    setSavingId(category.id);
    try {
      await onUpdate(category.id, { name: normalizedName, color: nextValues.color });
    } catch (error) {
      setDrafts((current) => ({
        ...current,
        [category.id]: { name: category.name, color: category.color as LibraryCategoryColorKey },
      }));
      toast.error((error as Error).message);
    } finally {
      setSavingId(null);
    }
  };

  const handleRenameKeyDown = async (event: KeyboardEvent<HTMLInputElement>, category: LibraryCategory) => {
    if (event.key !== "Enter") return;
    event.preventDefault();
    const draft = drafts[category.id];
    if (!draft) return;
    await commitUpdate(category, draft);
  };

  const handleDelete = async (category: LibraryCategory) => {
    if (!confirm(`Delete the category “${category.name}”?`)) return;
    setDeletingId(category.id);
    try {
      await onDelete(category.id);
      toast.success("Category deleted");
    } catch (error) {
      toast.error((error as Error).message);
    } finally {
      setDeletingId(null);
    }
  };

  const handleSetDefault = async (category: LibraryCategory) => {
    setDefaultingId(category.id);
    try {
      await onSetDefault(category.id);
      toast.success(`${category.name} is now the default category`);
    } catch (error) {
      toast.error((error as Error).message);
    } finally {
      setDefaultingId(null);
    }
  };

  const closeMerge = () => {
    setPendingMerge(null);
    setMergeTargetId(null);
  };

  const handleConfirmMerge = async () => {
    if (!pendingMerge || !mergeTargetId || !onMerge) return;
    const target = categories.find((c) => c.id === mergeTargetId);
    if (!target) return;
    setMerging(true);
    try {
      await onMerge(pendingMerge.id, mergeTargetId);
      toast.success(`Merged "${pendingMerge.name}" into "${target.name}"`);
      closeMerge();
    } catch (error) {
      toast.error((error as Error).message);
    } finally {
      setMerging(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Manage library categories</DialogTitle>
          <DialogDescription>Rename, recolor, merge, or remove the personal categories you use to organize your library.</DialogDescription>
        </DialogHeader>

        {onCreate ? (
          <div className="rounded-md border border-border bg-muted/30 p-4 space-y-3">
            <p className="text-sm font-medium text-foreground">Add a category</p>
            <div className="flex flex-wrap gap-2">
              <Input
                value={newName}
                onChange={(event) => setNewName(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    void handleCreate();
                  }
                }}
                placeholder="New category name"
                aria-label="New category name"
                disabled={creating}
                className="min-w-0 flex-1"
              />
              <Button type="button" onClick={() => void handleCreate()} disabled={creating || !newName.trim()}>
                {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Add category
              </Button>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {libraryCategoryPalette.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  aria-label={`New category color: ${option.label}`}
                  aria-pressed={newColor === option.value}
                  className={cn(
                    "h-7 w-7 rounded-full border transition hover:scale-105",
                    option.toneClassName,
                    newColor === option.value && "ring-2 ring-ring ring-offset-2 ring-offset-background",
                  )}
                  onClick={() => setNewColor(option.value)}
                  disabled={creating}
                />
              ))}
            </div>
          </div>
        ) : null}

        <div className="space-y-4">
          {!categories.length ? (
            <div className="rounded-md border border-dashed border-border p-6 text-sm text-muted-foreground">
              No categories yet. Add your first one above, or create one while editing a library item.
            </div>
          ) : (
            categories.map((category) => {
              const draft = drafts[category.id] ?? { name: category.name, color: category.color as LibraryCategoryColorKey };
              const isBusy = savingId === category.id || deletingId === category.id || defaultingId === category.id;
              const isDefault = defaultCategoryId === category.id;
              const usage = usageCounts?.[category.id] ?? 0;

              return (
                <div key={category.id} className="rounded-md border border-border bg-card p-4 space-y-3">
                  <div className="flex flex-wrap items-start gap-3">
                    <LibraryCategoryBadge category={{ name: draft.name.trim() || category.name, color: draft.color }} className="mt-1" />
                    <div className="min-w-0 flex-1 space-y-2">
                      <Input
                        value={draft.name}
                        onChange={(event) => updateDraft(category.id, { name: event.target.value })}
                        onBlur={() => void commitUpdate(category, draft)}
                        onKeyDown={(event) => void handleRenameKeyDown(event, category)}
                        placeholder="Category name"
                        disabled={isBusy}
                      />
                      <div className="flex items-center gap-2">
                        <Badge variant="secondary" className="shrink-0">
                          {usage} {usage === 1 ? "library item" : "library items"}
                        </Badge>
                        <p className="text-xs text-muted-foreground">Press Enter or click away to save the name.</p>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    {libraryCategoryPalette.map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        aria-label={option.label}
                        aria-pressed={draft.color === option.value}
                        className={cn(
                          "h-8 w-8 rounded-full border transition hover:scale-105",
                          option.toneClassName,
                          draft.color === option.value && "ring-2 ring-ring ring-offset-2 ring-offset-background",
                        )}
                        onClick={() => {
                          const nextDraft = { ...draft, color: option.value };
                          updateDraft(category.id, { color: option.value });
                          void commitUpdate(category, nextDraft);
                        }}
                        disabled={isBusy}
                      />
                    ))}
                  </div>
                  <div className="flex flex-wrap justify-between gap-2">
                    <p className="text-xs text-muted-foreground">{savingId === category.id ? "Updating…" : deletingId === category.id ? "Deleting…" : defaultingId === category.id ? "Saving default…" : "Changes appear in your library immediately."}</p>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        type="button"
                        variant={isDefault ? "secondary" : "outline"}
                        onClick={() => void handleSetDefault(category)}
                        disabled={isBusy || isDefault}
                      >
                        <CheckCircle2 className="h-4 w-4" /> {isDefault ? "Default category" : "Set as default"}
                      </Button>
                      {onMerge && (
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => {
                            setPendingMerge(category);
                            setMergeTargetId(null);
                          }}
                          disabled={isBusy || categories.length < 2}
                        >
                          <GitMerge className="h-4 w-4" /> Merge into…
                        </Button>
                      )}
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => void handleDelete(category)}
                        disabled={isBusy || isDefault}
                      >
                        <Trash2 className="h-4 w-4" /> Delete
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        <Dialog open={!!pendingMerge} onOpenChange={(next) => !next && closeMerge()}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Merge "{pendingMerge?.name}" into…</DialogTitle>
              <DialogDescription>
                All library items tagged with "{pendingMerge?.name}" will be moved to the chosen category. The original category will be deleted. This cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <Command>
              <CommandInput placeholder="Search categories…" />
              <CommandList>
                <CommandEmpty>No matches.</CommandEmpty>
                <CommandGroup>
                  {categories
                    .filter((c) => c.id !== pendingMerge?.id)
                    .map((c) => {
                      const usage = usageCounts?.[c.id] ?? 0;
                      return (
                        <CommandItem
                          key={c.id}
                          value={c.name}
                          onSelect={() => setMergeTargetId(c.id)}
                          className={mergeTargetId === c.id ? "bg-accent" : ""}
                        >
                          <span className="flex-1 truncate">{c.name}</span>
                          <Badge variant="secondary" className="ml-2 shrink-0">{usage}</Badge>
                          {mergeTargetId === c.id && <Check className="ml-2 h-4 w-4" />}
                        </CommandItem>
                      );
                    })}
                </CommandGroup>
              </CommandList>
            </Command>
            <DialogFooter>
              <Button variant="ghost" onClick={closeMerge} disabled={merging}>
                Cancel
              </Button>
              <Button
                onClick={handleConfirmMerge}
                disabled={merging || !mergeTargetId}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {merging ? <Loader2 className="h-4 w-4 animate-spin" /> : "Merge"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
};
